import {
  AfterViewChecked,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ConsentsTranslations } from '../../../shared/models/consents-translations';
import { SchemaTranslations } from '../../../shared/models/schema-translations';
import { Schema } from '../../../core/_models/schema';
import { SettingsService } from '../../../core/settings/settings.service';
import { Msisdn } from '../../../shared/models/msisdn';
import { ActivatedRoute } from '@angular/router';
import { MemberService } from '../../../core/members/member.service';
import { BaseParamsService } from '../../../core/base-params.service';
import { Settings } from '../../../core/_models/settings';
import { IdentifierType } from '../../../core/_models/member-interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { filter, map, publishLast, refCount } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '@app/core/store/reducers';
import { Observable, Subscription } from 'rxjs';
import { COOKIES_CONSENT_KEY } from '@app/core/cookie-consent/cookie-consent.service';
import { PropertiesFormComponent } from '@app/+profile-form/components/properties-form/properties-form.component';
import { JumptoErrorDirective } from '@app/shared/directives/jumpto-error.directive';
import { ApiTranslationsAttributes } from '@app/core/_models/api-translations';
import { LegalDocumentsService } from '@app/core/legal-documents/legal-documents.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { MsisdnInputComponent } from '@app/shared/components/msisdn-input/msisdn-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AdvancedTosDirective } from '@app/+profile-form/components/profile-form/advanced-tos.directive';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';
import { PrivacyPolicyComponent } from '@app/shared/components/privacy-policy/privacy-policy.component';
import { ConsentsComponent } from '@app/shared/components/consents/consents.component';
import { GetConsentsTranslationPipe } from '@app/shared/pipes/get-consents-translation.pipe';
import { AUTOLOGIN_ENCODED_MSISDN_QUERY_PARAM } from '@app/core/consts';
import { TranslationsService } from '@app/core/settings/translations.service';

export interface MemberFormData {
  consents: { [consentName: string]: boolean };

  [propertyName: string]: any;
}

@Component({
  selector: 'app-profile-form',
  templateUrl: './profile-form.component.html',
  imports: [
    AsyncPipe,
    JumptoErrorDirective,
    NgClass,
    MsisdnInputComponent,
    PropertiesFormComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    AdvancedTosDirective,
    InputErrorsComponent,
    PrivacyPolicyComponent,
    ConsentsComponent,
    GetConsentsTranslationPipe,
  ],
})
export class ProfileFormComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('appJumptoError') public appJumptoError: JumptoErrorDirective;
  @ViewChild(PropertiesFormComponent) public propertiesForm: PropertiesFormComponent;

  @Input()
  public set data(val: any) {
    if (val && Object.keys(val).length) {
      const { properties, consents } = this.memberService.processMemberToProfileForm(val);
      this.form.patchValue({ properties, consents });
    }
  }

  @Input() public isNewProfile = false;

  @Input()
  public set formErrors(value) {
    this._formErrors = value;
    if (this.appJumptoError) {
      this.form.setErrors(value);
      this.errorsToCheck = true;
    }
  }

  public get formErrors(): any {
    return this._formErrors;
  }

  @Output() public save = new EventEmitter<MemberFormData>();
  public form: UntypedFormGroup;
  public properties: Schema;
  public settings: Settings;
  public schemaTranslations: SchemaTranslations;
  public consentsTranslations: ConsentsTranslations;
  public settings$: Observable<Settings> = this.settingsService.getSettingsOnce().pipe(publishLast(), refCount());
  public source: string;
  public advancedTOS: SafeHtml;
  public isConsentsAvailable = false;
  public isFormSubmitted = false;
  public errorsToCheck = false;
  public _formErrors: any = {};

  protected readonly privacyPolicyHref = inject(LegalDocumentsService).getPrivacyPolicyHref();

  private cookieConsent$ = this.store.pipe(
    select(fromRoot.selectCoreContext),
    map((s) => s.cookieTrackingConsent),
    filter((v) => v != null),
  );
  private cookieConsentSub: Subscription;

  private readonly translationsService = inject(TranslationsService);

  constructor(formBuilder: UntypedFormBuilder,
              private settingsService: SettingsService,
              private memberService: MemberService,
              private baseParamsService: BaseParamsService,
              private route: ActivatedRoute,
              private sanitizer: DomSanitizer,
              private store: Store<fromRoot.State>) {
    this.form = formBuilder.group({
      toc: [false, Validators.required],
      properties: {},
      consents: {},
    }, {
      validator: this.profileFormValidator.bind(this),
    });

    this.translationsService.getTranslations([ApiTranslationsAttributes.consents, ApiTranslationsAttributes.member_properties])
      .subscribe(t => {
        this.schemaTranslations = t.getSchemaTranslations();
        this.consentsTranslations = t.getConsentsTranslations();
      });
  }

  public ngOnInit(): void {
    this.loadSchemaForForm();
    this.subscribeToCookieConsent();
  }

  public ngOnDestroy(): void {
    this.cookieConsentSub.unsubscribe();
  }

  public ngAfterViewChecked(): void {
    if (this.errorsToCheck) {
      this.appJumptoError.check();
      this.errorsToCheck = false;
    }
  }

  public onSubmit(event): void {
    event.preventDefault();
    this.isFormSubmitted = true;
    this.propertiesForm.form.updateValueAndValidity();

    if (this.form.valid && this.propertiesForm.form.valid) {
      const { properties, toc, ...value } = this.form.value;
      delete value.tos;
      this.save.emit({ ...value, ...properties });
    } else {
      setTimeout(() => this.appJumptoError.check());
    }
  }


  private loadSchemaForForm(): void {
    this.settings$.subscribe((settings) => this.onSettingsFetched(settings));
  }

  private onSettingsFetched(settings: Settings): void {

    this.settings = settings;
    this.source = this.baseParamsService.source;

    if (this.settings.advanced_terms_of_service_enabled) {
      this.advancedTOS = this.sanitizer.bypassSecurityTrustHtml(this.settings.advanced_terms_of_service);
    }

    if (!settings.schema.identifiers) {
      this.form.addControl(IdentifierType.MSISDN, new UntypedFormControl(''));
    }

    this.properties = new Schema(settings.schema);


    this.isConsentsAvailable = settings.schema.consents && !!Object.keys(settings.schema.consents).length;

    if (settings.schema.identifiers && this.isConsentsAvailable) {
      this.form.addControl('consents', new UntypedFormControl({}));
    }

    if (this.isConsentsAvailable) {
      this.form.addControl('tos', new FormControl(false, Validators.requiredTrue));
    }


    if (!this.isNewProfile) {
      return;
    }

    // Fill msisdn from queryparams on registration form
    this.route.queryParamMap.subscribe((params) => {
      const encodedMsisdn = params.get(IdentifierType.MSISDN) || params.get(AUTOLOGIN_ENCODED_MSISDN_QUERY_PARAM);
      const decodedMsisdn = Msisdn.decodeMsisdn(encodedMsisdn);

      let valuesToUpdate: any;

      if (this.properties.isMsisdnIdentifier()) {
        valuesToUpdate = { ...this.form.get('properties')?.value, msisdn: decodedMsisdn };
      } else {
        valuesToUpdate = { ...this.form.get('properties')?.value, email: params.get(IdentifierType.EMAIL) };
      }

      this.form.get('properties')?.patchValue(valuesToUpdate);
    });

  }

  private subscribeToCookieConsent(): void {
    this.cookieConsentSub = this.cookieConsent$.subscribe((enabled) => {
      const current = this.form.get('consents')?.value;

      if (current[COOKIES_CONSENT_KEY] != null) {
        return;
      }

      current[COOKIES_CONSENT_KEY] = enabled;
      this.form.get('consents')?.setValue(current);
    });
  }

  private profileFormValidator(formGroup: UntypedFormGroup): ValidationErrors | null {
    const errors = {};

    Object.keys(formGroup.controls).forEach(formControlName => {
      const formControl: AbstractControl | null = formGroup.get(formControlName);

      if (formControl?.errors?.required) {
        if (formControlName === 'tos') {
          errors[formControlName] = { error: 'not_accepted', property: formControlName };
        } else {
          errors[formControlName] = { error: 'not_contain_required_property', property: formControlName };
        }
      }
    });

    Object.assign(errors, this.propertiesForm?.form.errors || {});

    return Object.keys(errors).length ? errors : null;
  }
}
